import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Check, X, Zap, Bot, Shield } from "lucide-react";
import { RitualIcon } from "@/components/ui/RitualIcon";
import { Link, useNavigate } from "react-router-dom";
import { useState } from "react";
import { cn } from "@/lib/utils";
import { Logo } from "@/components/ui/Logo";
import { ThemeToggle } from "@/components/ui/theme-toggle";
import { PLAN_LIMITS, PlanTier } from "@/lib/plan-limits";
import { Footer } from "@/components/layout/Footer";
import { MetaTags } from "@/components/seo/MetaTags";
import { useRazorpay } from "@/hooks/use-razorpay";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

const Pricing = () => {
  const [currency, setCurrency] = useState<'INR' | 'USD'>('INR');
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'annual'>('monthly');
  const { initiatePayment, isLoading } = useRazorpay();
  const navigate = useNavigate();
  const { toast } = useToast();

  const handlePurchase = async (tier: PlanTier) => {
    if (tier === 'free') {
      navigate('/auth');
      return;
    }

    // Check if user is logged in
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      toast({
        title: "Sign in required",
        description: "Please sign in to upgrade your plan.",
      });
      navigate('/auth');
      return;
    }

    const success = await initiatePayment(tier as 'launch' | 'growth' | 'opspro', billingCycle);
    if (success) {
      navigate('/app/settings');
    }
  };

  const getAnnualPrice = (monthlyPrice: number) => Math.round(monthlyPrice * 12 * 0.8);

  const plans: Array<{
    tier: PlanTier;
    name: string;
    description: string;
    popular?: boolean;
  }> = [
    {
      tier: "free",
      name: "Free",
      description: "Try it out",
    },
    {
      tier: "launch",
      name: "Launch",
      description: "Perfect for small teams",
    },
    {
      tier: "growth",
      name: "Growth",
      description: "For growing teams",
      popular: true,
    },
    {
      tier: "opspro",
      name: "OpsPro",
      description: "Enterprise-grade",
    },
  ];

  const featureGroups = [
    {
      name: "Core Features",
      features: [
        { key: "seats", label: "Team members", getValue: (tier: PlanTier) => PLAN_LIMITS[tier].maxSeats === -1 ? "Unlimited" : `Up to ${PLAN_LIMITS[tier].maxSeats}` },
        { key: "sops", label: "SOPs", getValue: (tier: PlanTier) => PLAN_LIMITS[tier].maxSOPs === -1 ? "Unlimited" : `${PLAN_LIMITS[tier].maxSOPs}` },
        { key: "runs", label: "Runs/month", getValue: (tier: PlanTier) => PLAN_LIMITS[tier].maxRunsPerMonth === -1 ? "Unlimited" : `${PLAN_LIMITS[tier].maxRunsPerMonth}` },
        { key: "storage", label: "Storage", getValue: (tier: PlanTier) => `${PLAN_LIMITS[tier].maxStorageGB} GB` },
      ],
    },
    {
      name: "AI & Automation",
      features: [
        { key: "aiSopGeneration", label: "AI SOP generation", isFeature: true },
        { key: "aiAssistant", label: "AI assistant", isFeature: true },
        { key: "voiceInput", label: "Voice input", isFeature: true },
        { key: "aiCalls", label: "AI calls/day", getValue: (tier: PlanTier) => `${PLAN_LIMITS[tier].rateLimits.aiCallsPerDay}` },
      ],
    },
    {
      name: "Analytics & Insights",
      features: [
        { key: "advancedAnalytics", label: "Advanced analytics", isFeature: true },
        { key: "auditLogs", label: "Audit logs", isFeature: true },
      ],
    },
    {
      name: "Integrations",
      features: [
        { key: "apiAccess", label: "API access", isFeature: true },
        { key: "webhooks", label: "Webhooks", isFeature: true },
        { key: "customIntegrations", label: "Custom integrations", isFeature: true },
        { key: "sso", label: "SSO/SAML", isFeature: true },
      ],
    },
    {
      name: "Support",
      features: [
        { key: "support", label: "Support", getValue: (tier: PlanTier) => {
          if (PLAN_LIMITS[tier].features.dedicatedSupport) return "Dedicated";
          if (PLAN_LIMITS[tier].features.prioritySupport) return "Priority";
          return "Email";
        }},
        { key: "whiteLabel", label: "White-label", isFeature: true },
      ],
    },
  ];

  return (
    <div className="min-h-screen overflow-x-hidden flex flex-col">
      <MetaTags 
        title="Pricing - Ritual"
        description="Simple, transparent pricing for teams of all sizes. Start free, scale as you grow."
      />
      
      {/* Navigation */}
      <nav className="border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 sticky top-0 z-50">
        <div className="container flex h-14 sm:h-16 items-center justify-between px-4 sm:px-6">
          <Link to="/">
            <Logo size="sm" />
          </Link>
          <div className="flex items-center gap-2 sm:gap-4">
            <Link to="/" className="hidden sm:block text-sm font-medium hover:text-primary transition-colors">
              Home
            </Link>
            <ThemeToggle />
            <Link to="/auth">
              <Button variant="ghost" size="sm" className="hidden xs:inline-flex">Sign In</Button>
            </Link>
            <Link to="/auth">
              <Button size="sm" variant="hero">Start Free</Button>
            </Link>
          </div>
        </div>
      </nav>

      {/* Pricing Section */}
      <section className="py-12 sm:py-16 lg:py-24 flex-1">
        <div className="container px-4 sm:px-6">
          <div className="text-center mb-8 sm:mb-12 space-y-3">
            <Badge variant="secondary" className="mb-2">
              Simple pricing
            </Badge>
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold">Choose your plan</h1>
            <p className="text-base sm:text-lg text-muted-foreground max-w-xl mx-auto">
              Start free, scale as you grow
            </p>
          </div>

          {/* Billing Cycle Toggle */}
          <div className="flex flex-col sm:flex-row justify-center items-center gap-4 mb-8 sm:mb-12">
            <div className="inline-flex rounded-lg border p-1 bg-muted/30">
              <button
                onClick={() => setBillingCycle('monthly')}
                className={cn(
                  "px-3 sm:px-4 py-2 rounded-md text-xs sm:text-sm font-medium transition-all",
                  billingCycle === 'monthly' ? "bg-background shadow-sm" : "hover:bg-background/50"
                )}
              >
                Monthly
              </button>
              <button
                onClick={() => setBillingCycle('annual')}
                className={cn(
                  "px-3 sm:px-4 py-2 rounded-md text-xs sm:text-sm font-medium transition-all",
                  billingCycle === 'annual' ? "bg-background shadow-sm" : "hover:bg-background/50"
                )}
              >
                Annual
                <Badge variant="secondary" className="ml-2 text-xs">Save 20%</Badge>
              </button>
            </div>
            
            <div className="inline-flex rounded-lg border p-1 bg-muted/30">
              <button
                onClick={() => setCurrency('INR')}
                className={cn(
                  "px-3 sm:px-4 py-2 rounded-md text-xs sm:text-sm font-medium transition-all",
                  currency === 'INR' ? "bg-background shadow-sm" : "hover:bg-background/50"
                )}
              >
                INR
              </button>
              <button
                onClick={() => setCurrency('USD')}
                className={cn(
                  "px-3 sm:px-4 py-2 rounded-md text-xs sm:text-sm font-medium transition-all",
                  currency === 'USD' ? "bg-background shadow-sm" : "hover:bg-background/50"
                )}
              >
                USD
              </button>
            </div>
          </div>

          {/* Pricing Cards */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 max-w-6xl mx-auto">
            {plans.map((plan, i) => {
              const limits = PLAN_LIMITS[plan.tier];
              const monthlyPrice = currency === 'INR' ? limits.priceINR : limits.priceUSD;
              const displayPrice = billingCycle === 'annual' 
                ? Math.round(getAnnualPrice(monthlyPrice) / 12)
                : monthlyPrice;
              
              return (
                <Card 
                  key={i}
                  className={cn(
                    "relative",
                    plan.popular && "border-primary border-2 shadow-lg lg:scale-105"
                  )}
                >
                  {plan.popular && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                      <Badge variant="default" className="bg-gradient-primary shadow-glow text-xs">
                        <RitualIcon size="xs" className="mr-1" /> Popular
                      </Badge>
                    </div>
                  )}
                  <CardHeader className="p-4 sm:p-6">
                    <CardTitle className="text-lg sm:text-xl flex items-center gap-2">
                      {plan.name}
                      {plan.tier === 'opspro' && <Shield className="h-4 w-4 text-primary" />}
                    </CardTitle>
                    <CardDescription className="text-sm">{plan.description}</CardDescription>
                    <div className="mt-3 sm:mt-4">
                      <span className="text-3xl sm:text-4xl font-bold">
                        {currency === 'INR' ? '₹' : '$'}{displayPrice.toLocaleString()}
                      </span>
                      <span className="text-muted-foreground text-sm"> /month</span>
                    </div>
                    {billingCycle === 'annual' && plan.tier !== 'free' && (
                      <p className="text-xs text-primary mt-1">
                        Billed {currency === 'INR' ? '₹' : '$'}
                        {getAnnualPrice(monthlyPrice).toLocaleString()}/year
                      </p>
                    )}
                    <p className="text-xs sm:text-sm text-muted-foreground mt-1">
                      Up to {limits.maxSeats} members
                    </p>
                  </CardHeader>
                  <CardContent className="p-4 sm:p-6 pt-0">
                    <Button 
                      className="w-full mb-4 sm:mb-6" 
                      variant={plan.popular ? "hero" : plan.tier === 'free' ? "outline" : "default"}
                      size="lg"
                      onClick={() => handlePurchase(plan.tier)}
                      disabled={isLoading && plan.tier !== 'free'}
                    >
                      {isLoading && plan.tier !== 'free' ? 'Processing...' : 
                        plan.tier === 'free' ? 'Start Free' : 'Subscribe Now'}
                    </Button>
                    <ul className="space-y-2 sm:space-y-3">
                      <li className="flex items-start gap-2">
                        <Bot className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                        <span className="text-xs sm:text-sm">{limits.rateLimits.aiCallsPerDay} AI calls/day</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <Zap className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                        <span className="text-xs sm:text-sm">{limits.maxSOPs === -1 ? 'Unlimited' : limits.maxSOPs} SOPs</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <Check className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                        <span className="text-xs sm:text-sm">{limits.maxStorageGB}GB storage</span>
                      </li>
                      {limits.features.advancedAnalytics && (
                        <li className="flex items-start gap-2">
                          <Check className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                          <span className="text-xs sm:text-sm">Advanced analytics</span>
                        </li>
                      )}
                      {limits.features.apiAccess && (
                        <li className="flex items-start gap-2">
                          <Check className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                          <span className="text-xs sm:text-sm">API access</span>
                        </li>
                      )}
                      {limits.features.dedicatedSupport && (
                        <li className="flex items-start gap-2">
                          <Check className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                          <span className="text-xs sm:text-sm">Dedicated support</span>
                        </li>
                      )}
                    </ul>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Feature Comparison Table */}
          <div className="mt-16 sm:mt-24 max-w-6xl mx-auto">
            <h2 className="text-xl sm:text-2xl font-bold text-center mb-8">Compare Plans</h2>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-3 px-4 font-medium">Feature</th>
                    {plans.map((plan) => (
                      <th key={plan.tier} className="text-center py-3 px-4 font-medium min-w-[100px]">
                        {plan.name}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {featureGroups.map((group) => (
                    <>
                      <tr key={group.name} className="bg-muted/30">
                        <td colSpan={5} className="py-2 px-4 font-semibold text-xs uppercase tracking-wider text-muted-foreground">
                          {group.name}
                        </td>
                      </tr>
                      {group.features.map((feature) => (
                        <tr key={feature.key} className="border-b border-muted/50">
                          <td className="py-3 px-4">{feature.label}</td>
                          {plans.map((plan) => (
                            <td key={plan.tier} className="text-center py-3 px-4">
                              {feature.isFeature ? (
                                PLAN_LIMITS[plan.tier].features[feature.key as keyof typeof PLAN_LIMITS.free.features] ? (
                                  <Check className="h-4 w-4 text-primary mx-auto" />
                                ) : (
                                  <X className="h-4 w-4 text-muted-foreground/40 mx-auto" />
                                )
                              ) : (
                                <span className="text-xs">{feature.getValue?.(plan.tier)}</span>
                              )}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* FAQ */}
          <div className="mt-16 sm:mt-20 max-w-2xl mx-auto">
            <h2 className="text-xl sm:text-2xl font-bold text-center mb-8">FAQ</h2>
            <div className="space-y-4 sm:space-y-6">
              {[
                {
                  q: "Do I need a credit card?",
                  a: "No! Start free and add payment later."
                },
                {
                  q: "Can I change plans?",
                  a: "Yes, upgrade or downgrade anytime."
                },
                {
                  q: "What if I exceed my seat limit?",
                  a: "We'll notify you. Just upgrade to add more."
                },
                {
                  q: "What payment methods do you accept?",
                  a: "We accept all major credit/debit cards, UPI, and net banking via Razorpay."
                }
              ].map((faq, i) => (
                <div key={i} className="border-b pb-4 last:border-0">
                  <h3 className="text-sm sm:text-base font-semibold mb-1">{faq.q}</h3>
                  <p className="text-sm text-muted-foreground">{faq.a}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default Pricing;
